/**
 * NSFW reveal-state store — tab-scoped persistence of which image URLs
 * the viewer has chosen to reveal in this session. Shared across all
 * `<OptimizedImage>` variants (thumbnail, hero, avatar).
 *
 * Key behaviours per spec FR-023, FR-024:
 * - sessionStorage (per-tab) — NOT localStorage. Closing the tab forgets.
 * - LRU-evict at 200 entries to bound storage.
 * - Cleared on sign-out via `useNsfwRevealStore.getState().clear()` in
 *   the auth-provider logout path.
 *
 * Implementation note: Map-of-string-to-true gives O(1) reads and
 * insertion-ordered iteration for the LRU. JSON serialization round-trips
 * via the partialize/merge custom serializer.
 */

import { createJSONStorage, persist } from 'zustand/middleware';

import { createTrackedStore } from '@/lib/dev/zustand-registry';

const MAX_REVEALED = 200;
const STORAGE_KEY = 'morfi-nsfw-reveal';

export interface NsfwRevealState {
  /**
   * Insertion-ordered Map. Key = image URL. Value = true (sentinel).
   * Map preserves insertion order natively, so the first key is the
   * oldest entry — easy LRU eviction without an external library.
   */
  revealed: Map<string, true>;
  /** Mark a URL revealed in this session. */
  reveal: (url: string) => void;
  /** Drop a URL's revealed entry. Re-applies the blur on next render. */
  unreveal: (url: string) => void;
  /** Reactive query: has this URL been revealed? */
  isRevealed: (url: string) => boolean;
  /** Drop all revealed URLs. Called from auth-provider on sign-out. */
  clear: () => void;
}

interface PersistedState {
  entries: Array<[string, true]>;
}

// biome-ignore lint/nursery/useExplicitType: REASON: zustand's create + persist returns a complex generic UseBoundStore<...> chain that is more readable inferred.
export const useNsfwRevealStore = createTrackedStore<NsfwRevealState>('useNsfwRevealStore')(
  persist(
    (set: (next: Partial<NsfwRevealState>) => void, get: () => NsfwRevealState) => ({
      revealed: new Map<string, true>(),
      reveal(url: string): void {
        const next = new Map(get().revealed);
        // Move-to-end semantics: delete then re-insert so the URL becomes
        // the most-recently-touched entry even on a redundant reveal.
        next.delete(url);
        next.set(url, true);
        while (next.size > MAX_REVEALED) {
          const firstKey = next.keys().next().value;
          if (firstKey === undefined) break;
          next.delete(firstKey);
        }
        set({ revealed: next });
      },
      unreveal(url: string): void {
        const current = get().revealed;
        if (!current.has(url)) return;
        const next = new Map(current);
        next.delete(url);
        set({ revealed: next });
      },
      isRevealed(url: string): boolean {
        return get().revealed.has(url);
      },
      clear(): void {
        set({ revealed: new Map<string, true>() });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state: NsfwRevealState): PersistedState => ({
        entries: Array.from(state.revealed.entries()),
      }),
      // ALLOW-UNKNOWN: pass-through type for opaque test/store JSON
      merge: (persisted: unknown, current: NsfwRevealState): NsfwRevealState => {
        const persistedShape = persisted as PersistedState | undefined;
        const entries = persistedShape?.entries ?? [];
        return {
          ...current,
          revealed: new Map(entries),
        };
      },
    },
  ),
);
